import { gql } from "@apollo/client";
import { apolloCore } from "src/utils/apollo/apollo";
import {
  CreateWandQueryDocument,
  CreateWandQueryMutation,
  CreateWandQueryMutationVariables,
  DebugWandQueryDocument,
  DebugWandQueryMutation,
  DebugWandQueryMutationVariables,
  EditWandQueryDocument,
  EditWandQueryMutation,
  EditWandQueryMutationVariables,
  ExplainWandQueryDocument,
  ExplainWandQueryMutation,
  ExplainWandQueryMutationVariables,
  InsertWandCompletionDocument,
  InsertWandCompletionMutation,
  InsertWandCompletionMutationVariables,
  UpdateWandCompletionQueryDocument,
} from "src/gql/codegen/graphql";
import { Session, SessionWithUser } from "src/utils/users/types";
import { addToastNotification } from "shared/Toasts/toastNotificationStore";

export async function createWandQuery(
  prompt: string,
  queryId: number | undefined,
  session: SessionWithUser
) {
  try {
    const { data = null } = await apolloCore.mutate<
      CreateWandQueryMutation,
      CreateWandQueryMutationVariables
    >({
      mutation: CreateWandQueryDocument,
      variables: {
        prompt,
      },
      context: { session },
    });

    if (data === null) {
      throw new Error("No data returned");
    }

    const { response_id, query } = data.create_wand_query;

    await apolloCore.mutate<
      InsertWandCompletionMutation,
      InsertWandCompletionMutationVariables
    >({
      mutation: InsertWandCompletionDocument,
      variables: {
        id: response_id,
        question: prompt,
        answer: query,
        user_id: session.user.id,
        query_id: queryId,
      },
      context: { session },
    });

    return { response_id, query };
  } catch (error) {
    const description = error instanceof Error ? error.message : String(error);

    addToastNotification({
      level: "error",
      title: "Wand encountered an error",
      description,
    });
  }
}

export async function editWandQuery(
  prompt: string,
  query: string,
  session: Session
) {
  try {
    const { data = null } = await apolloCore.mutate<
      EditWandQueryMutation,
      EditWandQueryMutationVariables
    >({
      mutation: EditWandQueryDocument,
      variables: {
        prompt,
        query,
      },
      context: { session },
    });

    if (data === null) {
      throw new Error("No data returned");
    }

    return data.edit_wand_query;
  } catch (error) {
    const description = error instanceof Error ? error.message : String(error);

    addToastNotification({
      level: "error",
      title: "Wand encountered an error",
      description,
    });
  }
}

export async function debugWandQuery(
  query: string,
  error: string,
  session: Session
) {
  try {
    const { data = null } = await apolloCore.mutate<
      DebugWandQueryMutation,
      DebugWandQueryMutationVariables
    >({
      mutation: DebugWandQueryDocument,
      variables: { query, error },
      context: { session },
    });

    if (data === null) {
      throw new Error("No data returned");
    }

    return data.debug_wand_query;
  } catch (error) {
    const description = error instanceof Error ? error.message : String(error);

    addToastNotification({
      level: "error",
      title: "Wand encountered an error",
      description,
    });
  }
}

export async function explainWandQuery(
  name: string,
  description: string,
  query: string,
  session: Session
) {
  const { data = null } = await apolloCore.mutate<
    ExplainWandQueryMutation,
    ExplainWandQueryMutationVariables
  >({
    mutation: ExplainWandQueryDocument,
    variables: { name, description, query },
    context: { session },
  });

  if (data === null) {
    throw new Error("No data returned");
  }

  return data.explain_wand_query;
}

export async function updateCompletionQuery(
  id: string | undefined,
  queryId: number,
  session: Session
) {
  if (id === undefined) {
    return;
  }

  await apolloCore.mutate({
    mutation: UpdateWandCompletionQueryDocument,
    variables: {
      id,
      query_id: queryId,
    },
    context: { session },
  });
}

gql`
  mutation CreateWandQuery($prompt: String!) {
    create_wand_query(prompt: $prompt) {
      response_id
      query
    }
  }
`;

gql`
  mutation EditWandQuery($prompt: String!, $query: String!) {
    edit_wand_query(prompt: $prompt, query: $query) {
      response_id
      query
    }
  }
`;

gql`
  mutation DebugWandQuery($error: String!, $query: String!) {
    debug_wand_query(error: $error, query: $query) {
      response_id
      query
    }
  }
`;

gql`
  mutation ExplainWandQuery(
    $name: String!
    $description: String!
    $query: String!
  ) {
    explain_wand_query(name: $name, description: $description, query: $query) {
      response_id
      explanation
    }
  }
`;

gql`
  mutation InsertWandCompletion(
    $id: uuid!
    $question: String!
    $answer: String!
    $user_id: Int!
    $query_id: Int
    $query_version: Int
  ) {
    insert_wand_completions_one(
      object: {
        id: $id
        question: $question
        answer: $answer
        user_id: $user_id
        query_id: $query_id
        query_version: $query_version
      }
    ) {
      id
    }
  }
`;

gql`
  mutation UpdateWandCompletionQuery(
    $id: uuid!
    $query_id: Int
    $query_version: Int
  ) {
    update_wand_completions(
      where: { id: { _eq: $id } }
      _set: { query_id: $query_id, query_version: $query_version }
    ) {
      affected_rows
    }
  }
`;
